import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Gallery Traffic — UniFi Protect line-crossing people counter.
// Live occupancy per gallery + FIFO-estimated visits (entered/exited/dwell) +
// a Setup panel with the webhook to paste into Protect's Alarm Manager.
// Rendered as a tab inside the Facilities hub (Facilities gate applies).

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';
const GREEN = '#047857';

const card = { background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const label = { fontSize: '11.5px', fontWeight: 600, color: '#8A8A8E', textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { width: '100%', padding: '9px 11px', border: '1px solid #dfe3ea', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' };

function fmtDwell(s) {
  if (s === null || s === undefined) return '—';
  s = Math.round(s);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}
function fmtTime(t) {
  if (!t) return '—';
  const d = new Date(t);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtClock(t) {
  if (!t) return null;
  return new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
// Full date + precise (to-the-second) local time — used in the hover detail rows.
function fmtDateTimeFull(t) {
  if (!t) return '—';
  const d = new Date(t);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px', gap: '16px', flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0', maxWidth: 640 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
function PrimaryBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', background: disabled ? '#F0B48C' : ORANGE, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: disabled ? 'default' : 'pointer' }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick, tone }) {
  return (
    <button onClick={onClick}
      style={{ padding: '8px 13px', background: '#fff', border: `1px solid ${tone === 'danger' ? '#f2c4c4' : '#e2e6ee'}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: tone === 'danger' ? '#b91c1c' : NAVY, cursor: 'pointer' }}>
      {children}
    </button>
  );
}

export default function GalleryTraffic() {
  const [view, setView] = useState('live'); // live | visits | setup
  const tabs = [{ k: 'live', l: 'Live' }, { k: 'visits', l: 'Visit Log' }, { k: 'setup', l: 'Setup' }];
  return (
    <div>
      <div style={{ display: 'inline-flex', gap: 4, background: '#F1F3F7', padding: 4, borderRadius: 10, marginBottom: 22 }}>
        {tabs.map(t => (
          <button key={t.k} onClick={() => setView(t.k)}
            style={{ padding: '7px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: view === t.k ? '#fff' : 'transparent', color: view === t.k ? NAVY : '#7A7F8A',
              boxShadow: view === t.k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            {t.l}
          </button>
        ))}
      </div>
      {view === 'live' && <LiveView />}
      {view === 'visits' && <VisitsView />}
      {view === 'setup' && <SetupView />}
    </div>
  );
}

// ---- Hover detail: recent crossings OR dwell sessions for one gallery ----
function DetailPanel({ rows, kind }) {
  const dwell = kind === 'dwell';
  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, width: 340, maxWidth: '92vw',
      background: '#fff', border: '1px solid #E6E8EC', borderRadius: 12,
      boxShadow: '0 14px 36px rgba(15,23,42,0.20)', zIndex: 60, padding: '12px 6px 8px',
      maxHeight: 320, overflowY: 'auto',
    }}>
      <div style={{ ...label, padding: '0 10px 8px' }}>{dwell ? 'Recent dwell sessions' : 'Recent crossings'}</div>
      {rows === undefined && <div style={{ fontSize: 12.5, color: '#9CA3AF', padding: '6px 10px' }}>Loading…</div>}
      {rows && rows.length === 0 && <div style={{ fontSize: 12.5, color: '#9CA3AF', padding: '6px 10px' }}>{dwell ? 'No dwell sessions yet.' : 'No crossings yet.'}</div>}
      {(rows || []).map(r => dwell ? (
        <div key={r.id} style={{ padding: '7px 10px', borderTop: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>{fmtDateTimeFull(r.entered_at)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11.5, color: '#6B7280', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, background: '#EEF2F7', borderRadius: 5, padding: '1px 7px' }}>{fmtDwell(r.dwell_seconds)}</span>
            <span>{r.exited_at ? `ended ${fmtClock(r.exited_at)}` : 'ongoing'}</span>
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: r.status === 'open' ? '#1d4ed8' : GREEN }}>
                {r.status === 'open' ? 'here now' : 'left'}
              </span>
              {r.protect_link && <a href={r.protect_link} target="_blank" rel="noreferrer" style={{ color: NAVY, fontWeight: 600, textDecoration: 'none' }}>Open in Protect ↗</a>}
            </span>
          </div>
        </div>
      ) : (
        <div key={r.id} style={{ padding: '7px 10px', borderTop: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>{fmtDateTimeFull(r.occurred_at)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11.5, color: '#6B7280', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: r.direction === 'in' ? GREEN : ORANGE, background: r.direction === 'in' ? '#ECFDF5' : '#FFF1E9', borderRadius: 5, padding: '1px 7px' }}>{r.direction}</span>
            {r.camera_name && <span>{r.camera_name}</span>}
            {r.score != null && <span>· {r.score}% conf</span>}
            {r.source === 'simulate' && <span style={{ color: '#9CA3AF' }}>· test</span>}
            {r.protect_link && <a href={r.protect_link} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', color: NAVY, fontWeight: 600, textDecoration: 'none' }}>Open in Protect ↗</a>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- LIVE: per-gallery count cards with hover detail + management ----
function LiveView() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState('');
  const [managing, setManaging] = useState(false);
  const [hover, setHover] = useState(null);   // zone id whose detail is open
  const [detail, setDetail] = useState({});   // zoneId -> crossings[] (undefined = loading)

  const load = useCallback(() => {
    axios.get(API + '/gallery-occupancy/summary', { headers: headers() })
      .then(r => { setSummary(r.data); setErr(''); })
      .catch(() => setErr('Failed to load occupancy'));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, [load]);

  // Fetch the hover-detail rows for a zone (crossings or dwell sessions).
  const fetchDetail = useCallback((zone) => {
    const path = zone.has_dwell ? 'visits' : 'crossings';
    axios.get(API + `/gallery-occupancy/${path}?zone_id=${zone.id}&limit=12`, { headers: headers() })
      .then(r => setDetail(d => ({ ...d, [zone.id]: r.data })))
      .catch(() => {});
  }, []);

  // Keep the OPEN hover panel live: fetch when a card is hovered, and re-fetch on
  // every summary refresh while it stays open (so its rows auto-refresh too).
  useEffect(() => {
    if (hover == null || !summary) return;
    const zone = summary.find(z => z.id === hover);
    if (zone) fetchDetail(zone);
  }, [hover, summary, fetchDetail]);

  return (
    <div>
      <SectionHeader
        title="Gallery Traffic"
        subtitle="Live people-counting from UniFi Protect line crossings. Times are the real event time from the camera. Hover a card for recent crossings."
        action={<div style={{ display: 'flex', gap: 8 }}>
          <GhostBtn onClick={load}>↻ Refresh</GhostBtn>
          <PrimaryBtn onClick={() => setManaging(true)}>Manage galleries</PrimaryBtn>
        </div>} />

      {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{err}</div>}

      {summary && summary.length === 0 && (
        <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>No galleries configured yet</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', margin: '6px 0 16px' }}>Add a gallery and wire its entrance lines to start counting.</div>
          <PrimaryBtn onClick={() => setManaging(true)}>Manage galleries</PrimaryBtn>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
        {(summary || []).map(z => {
          const open = hover === z.id;
          const dwell = z.has_dwell;
          const last = fmtClock(z.last_crossing_at);
          const hereNow = z.occupancy > 0;
          return (
            <div key={z.id}
              onMouseEnter={() => setHover(z.id)} onMouseLeave={() => setHover(null)}
              style={{
                ...card, padding: '18px 20px', position: 'relative',
                opacity: z.is_active ? 1 : 0.6, zIndex: open ? 40 : 1,
                boxShadow: open ? '0 8px 24px rgba(0,0,0,0.10)' : card.boxShadow,
                transition: 'box-shadow .15s',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.name}</div>
                {dwell && <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: ORANGE, background: '#FFF1E9', borderRadius: 5, padding: '1px 7px' }}>dwell</span>}
                {!z.is_active && <span style={{ fontSize: 10, color: '#9CA3AF' }}>paused</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
                <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1, color: dwell && hereNow ? '#1d4ed8' : NAVY }}>{dwell ? z.occupancy : z.today_in}</div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>{dwell ? 'viewing now' : 'crossings today'}</div>
              </div>
              {dwell ? (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: hereNow ? '#1d4ed8' : (z.today_visits ? GREEN : '#D1D5DB') }} />
                  {z.today_visits || 0} visit{z.today_visits === 1 ? '' : 's'} today
                  {z.avg_dwell_seconds != null && <span> · avg {fmtDwell(z.avg_dwell_seconds)}</span>}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: last ? GREEN : '#D1D5DB' }} />
                  {last ? `Last crossing ${last}` : 'No crossings yet'}
                </div>
              )}
              {open && <DetailPanel rows={detail[z.id]} kind={dwell ? 'dwell' : 'crossing'} />}
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 16 }}>Live — refreshes every ~3s. Timestamps are the camera's real detection time. Hover any gallery to see its recent crossings / dwell sessions with date, exact time, and duration.</p>

      {managing && <ManageModal onClose={() => { setManaging(false); load(); }} />}
    </div>
  );
}

// ---- Manage galleries + lines + test tool (modal) ----
function ManageModal({ onClose }) {
  const [zones, setZones] = useState([]);
  const [lines, setLines] = useState([]);
  const [msg, setMsg] = useState('');
  const [nz, setNz] = useState({ name: '', capacity: '' });
  const [nl, setNl] = useState({ zone_id: '', direction: 'in', match_key: '', camera_name: '' });

  const load = useCallback(() => {
    Promise.all([
      axios.get(API + '/gallery-occupancy/zones', { headers: headers() }),
      axios.get(API + '/gallery-occupancy/lines', { headers: headers() }),
    ]).then(([z, l]) => { setZones(z.data); setLines(l.data); }).catch(() => setMsg('Load failed'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addZone = () => {
    if (!nz.name.trim()) return;
    axios.post(API + '/gallery-occupancy/zones', nz, { headers: headers() })
      .then(() => { setNz({ name: '', capacity: '' }); load(); })
      .catch(e => setMsg(e.response?.data?.error || 'Failed to add gallery'));
  };
  const delZone = (id) => {
    if (!window.confirm('Delete this gallery and all its lines/data?')) return;
    axios.delete(API + '/gallery-occupancy/zones/' + id, { headers: headers() }).then(load);
  };
  const resetZone = (id) => {
    axios.post(API + `/gallery-occupancy/zones/${id}/reset`, {}, { headers: headers() })
      .then(r => { setMsg(`Reset — closed ${r.data.closed} open visit(s).`); load(); });
  };
  const addLine = () => {
    if (!nl.zone_id || !nl.match_key.trim()) { setMsg('Pick a gallery and enter a key'); return; }
    axios.post(API + '/gallery-occupancy/lines', nl, { headers: headers() })
      .then(() => { setNl({ zone_id: nl.zone_id, direction: 'in', match_key: '', camera_name: '' }); setMsg(''); load(); })
      .catch(e => setMsg(e.response?.data?.error || 'Failed to add line'));
  };
  const delLine = (id) => axios.delete(API + '/gallery-occupancy/lines/' + id, { headers: headers() }).then(load);

  const simulate = (line) => {
    axios.post(API + '/gallery-occupancy/simulate', { key: line.match_key }, { headers: headers() })
      .then(r => setMsg(`Simulated ${line.direction.toUpperCase()} on "${line.zone_name}" — occupancy now ${r.data.occupancy} (${r.data.visitAction}).`))
      .catch(e => setMsg(e.response?.data?.error || 'Simulate failed'));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ ...card, width: 'min(760px, 100%)', padding: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 19, fontWeight: 700, color: NAVY, margin: 0 }}>Manage Galleries &amp; Lines</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>
        {msg && <div style={{ fontSize: 12.5, color: NAVY, background: '#F1F6FB', border: '1px solid #DCE7F2', borderRadius: 8, padding: '8px 11px', margin: '10px 0' }}>{msg}</div>}

        {/* Galleries */}
        <div style={{ ...label, marginTop: 16, marginBottom: 8 }}>Galleries</div>
        {zones.map(z => (
          <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F1F1F4' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{z.name}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{z.capacity ? `capacity ${z.capacity}` : 'no capacity set'}</div>
            </div>
            <GhostBtn onClick={() => resetZone(z.id)}>Reset count</GhostBtn>
            <GhostBtn onClick={() => delZone(z.id)} tone="danger">Delete</GhostBtn>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}><div style={{ ...label, marginBottom: 4 }}>New gallery name</div>
            <input style={inputStyle} value={nz.name} onChange={e => setNz({ ...nz, name: e.target.value })} placeholder="e.g. African Art Gallery" /></div>
          <div style={{ width: 120 }}><div style={{ ...label, marginBottom: 4 }}>Capacity</div>
            <input style={inputStyle} value={nz.capacity} onChange={e => setNz({ ...nz, capacity: e.target.value })} placeholder="opt." /></div>
          <PrimaryBtn onClick={addZone}>Add</PrimaryBtn>
        </div>

        {/* Lines */}
        <div style={{ ...label, marginTop: 26, marginBottom: 8 }}>Crossing lines (two per doorway: one IN, one OUT)</div>
        {lines.map(l => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F1F1F4' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: l.direction === 'in' ? GREEN : ORANGE, background: l.direction === 'in' ? '#ECFDF5' : '#FFF1E9', borderRadius: 5, padding: '2px 8px', textTransform: 'uppercase' }}>{l.direction}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: NAVY }}>{l.zone_name}{l.camera_name ? ` · ${l.camera_name}` : ''}</div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>key: <code>{l.match_key}</code></div>
            </div>
            <GhostBtn onClick={() => simulate(l)}>Test</GhostBtn>
            <GhostBtn onClick={() => delLine(l.id)} tone="danger">Delete</GhostBtn>
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.8fr 1fr 1.2fr auto', gap: 8, marginTop: 12, alignItems: 'end' }}>
          <div><div style={{ ...label, marginBottom: 4 }}>Gallery</div>
            <select style={inputStyle} value={nl.zone_id} onChange={e => setNl({ ...nl, zone_id: e.target.value })}>
              <option value="">Select…</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select></div>
          <div><div style={{ ...label, marginBottom: 4 }}>Direction</div>
            <select style={inputStyle} value={nl.direction} onChange={e => setNl({ ...nl, direction: e.target.value })}>
              <option value="in">In</option><option value="out">Out</option>
            </select></div>
          <div><div style={{ ...label, marginBottom: 4 }}>Match key</div>
            <input style={inputStyle} value={nl.match_key} onChange={e => setNl({ ...nl, match_key: e.target.value })} placeholder="african-in" /></div>
          <div><div style={{ ...label, marginBottom: 4 }}>Camera label</div>
            <input style={inputStyle} value={nl.camera_name} onChange={e => setNl({ ...nl, camera_name: e.target.value })} placeholder="North door" /></div>
          <PrimaryBtn onClick={addLine}>Add</PrimaryBtn>
        </div>
        <p style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 12 }}>The <strong>match key</strong> is what each Protect webhook sends to identify the line — use a short unique slug per line (e.g. <code>african-in</code>, <code>african-out</code>). See the <strong>Setup</strong> tab for the webhook to paste into Protect.</p>
      </div>
    </div>
  );
}

// ---- VISITS: recent FIFO-paired visit rows ----
function VisitsView() {
  const [rows, setRows] = useState(null);
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState('');

  useEffect(() => { axios.get(API + '/gallery-occupancy/zones', { headers: headers() }).then(r => setZones(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const q = zoneId ? `?zone_id=${zoneId}` : '';
    axios.get(API + '/gallery-occupancy/visits' + q, { headers: headers() }).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [zoneId]);

  return (
    <div>
      <SectionHeader title="Visit Log" subtitle="Each row pairs an entry with an exit (FIFO order). Durations are estimates, not per-person tracking."
        action={
          <select style={{ ...inputStyle, width: 220 }} value={zoneId} onChange={e => setZoneId(e.target.value)}>
            <option value="">All galleries</option>
            {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>} />
      <div style={{ ...card, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>{['Gallery', 'Entered', 'Exited', 'Est. dwell', 'Status', ''].map((c, i) =>
              <th key={c || i} style={{ background: NAVY, color: '#fff', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '11px 14px' }}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows && rows.length === 0 && <tr><td colSpan={6} style={{ padding: '40px 14px', textAlign: 'center', color: '#9CA3AF' }}>No visits recorded yet.</td></tr>}
            {(rows || []).map(v => (
              <tr key={v.id} style={{ borderTop: '1px solid #F1F1F4' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: NAVY }}>{v.zone_name}</td>
                <td style={{ padding: '10px 14px' }}>{fmtTime(v.entered_at)}</td>
                <td style={{ padding: '10px 14px' }}>{fmtTime(v.exited_at)}</td>
                <td style={{ padding: '10px 14px' }}>{fmtDwell(v.dwell_seconds)}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px', textTransform: 'uppercase',
                    color: v.status === 'open' ? '#1d4ed8' : GREEN, background: v.status === 'open' ? '#EFF6FF' : '#ECFDF5' }}>
                    {v.status === 'open' ? 'In gallery' : 'Complete'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {v.protect_link && <a href={v.protect_link} target="_blank" rel="noreferrer" style={{ color: NAVY, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>Open in Protect ↗</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- SETUP: webhook + per-line payloads to paste into Protect ----
function SetupView() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(API + '/gallery-occupancy/config', { headers: headers() }).then(r => setCfg(r.data)).catch(() => {}); }, []);
  const webhookUrl = API + '/gallery-occupancy/webhook';

  return (
    <div>
      <SectionHeader title="Setup — UniFi Protect" subtitle="Wire each gallery doorway with two line-crossing alarms (one In, one Out) in Protect's Alarm Manager, each sending a webhook here." />

      <div style={{ ...card, padding: 22, marginBottom: 16 }}>
        <div style={{ ...label, marginBottom: 8 }}>Steps in UniFi Protect</div>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 1.9 }}>
          <li>On a camera covering a gallery entrance, add a <strong>Smart Detection → Line Crossing</strong> and draw a line across the doorway; set object type <strong>Person</strong>. Use the crossing direction to distinguish entering vs leaving (or draw one line per direction).</li>
          <li>Go to <strong>Alarm Manager → Create Alarm</strong>. Trigger = that <strong>Line Crossing</strong> (person) on that camera/line. Action = <strong>Webhook → Custom Webhook</strong>.</li>
          <li>Paste that line's <strong>full URL</strong> from the list below — it already contains the secret + key. Method <strong>GET or POST</strong> both work; no body needed (Protect's body is fixed and ignored here).</li>
          <li>Repeat for every In and Out line. Each URL's <code>key</code> must match a line you added under <strong>Manage galleries</strong>.</li>
        </ol>
      </div>

      <div style={{ ...card, padding: 22, marginBottom: 16 }}>
        <div style={{ ...label, marginBottom: 8 }}>Webhook URL format (GET or POST)</div>
        <code style={{ display: 'block', background: '#0F1B2A', color: '#E6EDF3', padding: '10px 12px', borderRadius: 8, fontSize: 12.5, wordBreak: 'break-all' }}>{webhookUrl}?secret=<span style={{ color: '#F0B48C' }}>&lt;SECRET&gt;</span>&amp;key=<span style={{ color: '#F0B48C' }}>&lt;line key&gt;</span></code>
        <p style={{ fontSize: 12.5, color: '#374151', marginTop: 10 }}>Protect's webhook body can't be customized, so the <strong>secret</strong> and line <strong>key</strong> travel in the URL. Use the ready-made per-line URLs below. (Alternatively, send the secret as an <code>x-webhook-secret</code> header instead of in the URL.)</p>
        <p style={{ fontSize: 12, color: cfg && cfg.webhook_secret_configured ? GREEN : '#b91c1c', marginTop: 10 }}>
          {cfg ? (cfg.webhook_secret_configured
            ? '✓ Webhook secret is configured on the server.'
            : '✗ GALLERY_WEBHOOK_SECRET is NOT set on the server — add it to the backend environment before Protect can post.') : 'Checking server config…'}
        </p>
      </div>

      <div style={{ ...card, padding: 22 }}>
        <div style={{ ...label, marginBottom: 10 }}>Per-line webhook URLs (copy into Protect)</div>
        {cfg && cfg.lines.length === 0 && <div style={{ fontSize: 13, color: '#9CA3AF' }}>No lines yet — add them under the Live tab → “Manage galleries”.</div>}
        {(cfg?.lines || []).map(l => {
          const url = `${webhookUrl}?secret=<SECRET>&key=${l.match_key}`;
          return (
            <div key={l.id} style={{ padding: '9px 0', borderBottom: '1px solid #F1F1F4' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, marginBottom: 5 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: l.direction === 'in' ? GREEN : ORANGE, textTransform: 'uppercase', width: 34 }}>{l.direction}</span>
                <span style={{ flex: 1, color: NAVY, fontWeight: 600 }}>{l.zone_name}{l.camera_name ? ` · ${l.camera_name}` : ''}</span>
                <GhostBtn onClick={() => navigator.clipboard?.writeText(url)}>Copy</GhostBtn>
              </div>
              <code style={{ display: 'block', background: '#F4F6F9', borderRadius: 6, padding: '6px 9px', fontSize: 11.5, wordBreak: 'break-all', color: '#374151' }}>{url}</code>
            </div>
          );
        })}
        <p style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 10 }}>Replace <code>&lt;SECRET&gt;</code> with your GALLERY_WEBHOOK_SECRET before pasting into Protect. Keys are managed under Live → “Manage galleries”.</p>
      </div>
    </div>
  );
}
